// Phase 3 candidate video upload — end-of-interview sequence + retrying upload (unit-tested).
//
// Interview 56 lost its video: the upload started only AFTER /end returned, was skipped
// entirely without a conversation ID, ran invisibly, and the page said "You may safely close
// this window" while it was still running. This module fixes the ordering and adds retries;
// the page shows progress and warns before closing. (Chunked upload during the interview
// replaces this in the recording-merge deploy.)

export const UPLOAD_RETRIES = 3;                       // retries AFTER the first attempt
export const RETRY_DELAYS_MS = [2000, 5000, 10000];

// FastAPI's 400 when the multipart body stops before its end (interview 56) — the request was
// cut short in transit, so a fresh attempt can succeed. Our own 400s (status / file type /
// too small) carry other messages and are NOT retried.
export const BODY_CUT_SHORT_DETAIL = 'There was an error parsing the body';

/** Worth retrying: network error / abort (0), timeout, rate limit, any 5xx, a body cut short. */
export function isRetryable(status, error = '') {
  if (status === 400) return String(error).includes(BODY_CUT_SHORT_DETAIL);
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

/**
 * Run sendOnce() until it succeeds, a non-retryable status comes back, or the retries run out.
 * sendOnce(attempt) resolves { ok, status, error }. Returns { ok, attempts, last }.
 */
export async function uploadWithRetry(sendOnce, {
  retries = UPLOAD_RETRIES,
  delays = RETRY_DELAYS_MS,
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  onRetry,
} = {}) {
  let last = null;
  let attempts = 0;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (attempt > 0) {
      if (onRetry) onRetry(attempt, last);
      await sleep(delays[Math.min(attempt - 1, delays.length - 1)]);
    }
    attempts += 1;
    last = await sendOnce(attempt);
    if (last.ok) return { ok: true, attempts, last };
    if (!isRetryable(last.status, last.error)) break;
  }
  return { ok: false, attempts, last };
}

/**
 * One multipart POST with upload progress (fetch has none). Never rejects.
 * NO timeout: a 75-minute recording is ~280 MB and may take many minutes on a slow uplink.
 * (Only this upload — other API calls are unaffected.)
 */
export function xhrUpload(url, formData, onProgress) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = 0;   // explicit: never time out on the client side
    xhr.ontimeout = () => resolve({ ok: false, status: 0, error: 'upload timed out' });
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let detail = '';
      try { detail = JSON.parse(xhr.responseText || '{}').detail || ''; } catch (e) { /* not JSON */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status,
                error: xhr.status >= 300 ? `HTTP ${xhr.status}${detail ? `: ${detail}` : ''}` : '' });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, error: 'network error' });
    xhr.onabort = () => resolve({ ok: false, status: 0, error: 'upload aborted' });
    xhr.send(formData);
  });
}

/**
 * End-of-interview ordering. The video upload starts as soon as the recording is stopped —
 * BEFORE /end and even without a conversation ID (it only needs the interview ID + passcode).
 * Returns { upload, end }: promises (or null) the caller can await independently.
 */
export async function runEndSequence({ endSession, stopRecording, startUpload, conversationId, callEnd }) {
  try { await endSession(); } catch (e) { /* already closed */ }
  let blob = null;
  try { blob = await stopRecording(); } catch (e) { /* recorder failed — nothing to upload */ }
  const upload = blob ? startUpload(blob) : null;
  const end = conversationId ? callEnd(conversationId) : null;
  return { upload, end };
}
